import { Link } from 'react-router-dom';
import {
  Card, CardActionArea, Chip, Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getImageSmallUrl } from '@sda/utils/image';
import { Clamp } from '@client/components/common/Clamp';
import { Media } from '@client/components/common/Media';
import { useFirestore } from '@client/hooks/utils.hooks';
import { generatePricingInformation } from '@sda/utils/meta';
import { formatTotalAsCurrency } from '@sda/utils/lang';
import type { Currencies } from '@sda/enums';
import type { PublicImageUrl } from '@sda/interfaces/image';

const useStyles = makeStyles({ name: 'ConnectCard' })((theme) => ({
  root: {
    borderRadius: theme.radii.large,
    position: 'relative',

    [theme.breakpoints.down('md')]: {
      width: '100%',
    },

    backgroundColor: 'transparent',
    boxShadow: 'none',
    overflow: 'visible',
  },
  media: {
    marginBottom: theme.spacing(1.5),
    top: 1,
    borderRadius: theme.radii.large,
    overflow: 'hidden',
  },
  mediaContainer: {
    position: 'relative',
    width: '100%',

    '&:hover > div:first-of-type': {
      boxShadow: theme.shadows[5],
    },
  },
  ticketPrice: {
    position: 'absolute',
    bottom: 0,
    right: 0,
  },
}));

interface ShowCardProps {
  readonly showId: string;
  readonly path: string;
  readonly imgSrc: string | PublicImageUrl[] | string[];
  readonly title: string;
  readonly location: string | React.ReactNode;
  readonly startTime: string;
  readonly currency: Currencies;
}

export const ShowCard: React.FC<ShowCardProps> = ({
  showId, imgSrc, path, title,
  currency, location, startTime,
}) => {
  const { classes, cx } = useStyles();

  const [products] = useFirestore(
    (SDK) => SDK.Products.listBy((query) => [
      query.isEqualTo('showId', showId),
      query.isTrue('isActive'),
    ]),
    {
      key: showId,
    },
  );

  const pricingInformation = generatePricingInformation(Object.values(products || []));

  return (
    <Card className={classes.root}>
      <CardActionArea
        component={Link}
        to={path}
        className={cx(classes.mediaContainer, 'main-home-shows')}
      >
        <Media
          className={classes.media}
          images={imgSrc}
          sizeCallback={getImageSmallUrl}
          title={title}
          enableCarousel
        />

        <div>
          <Clamp
            variant="body2"
            lines={1}
            paragraph
          >
            {title}
          </Clamp>

          <Typography
            variant="body1"
            color="textSecondary"
          >
            {location}
          </Typography>

          <Typography
            variant="body1"
            color="textSecondary"
          >
            {startTime}
          </Typography>
        </div>

        <Chip
          size="small"
          color="success"
          className={classes.ticketPrice}
          label={pricingInformation ? (
            <>
              {pricingInformation.minimumPrice === 0 && !pricingInformation.hasVariablePrice
                ? 'Free'
                : formatTotalAsCurrency(pricingInformation.minimumPrice, currency)}

              {Boolean(pricingInformation.hasVariablePrice) && '+'}
            </>
          ) : 'Free'}
        />
      </CardActionArea>
    </Card>
  );
};
